import React, { useContext } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import qs from 'qs'
import { get } from 'lodash'
import { Redirect } from 'react-router-dom'

// Utils
import { buildUrlParamObj } from 'src/helpers/utils'
import { redirect } from 'src/helpers/browser'

import { parseJWT, setClientToken, getUserIdFromToken } from 'src/auth'
import { ExpContext } from 'src/providers/experimentation'

// Actions
import { setReferrerSource } from 'src/actions/trackingSource'
import { fetchCustomMailer } from 'src/actions/customMailer'
import { flagClientForUnsubscribe } from 'src/actions/client'

import { toggleDisclaimer } from 'src/actions/layout'
import {
  unsubscribeFromHomeDigestFlow,
  unsubscribeFromHomeMarketFlow,
  unsubscribeFromHomeownerEmailsFlow,
  unsubscribeFromListingsFlow,
  unsubscribeFromCustomerFlow,
} from 'src/constants/unsubscribe'

// Constants
import { AUTH } from 'src/constants'
import { PATH_HELPERS } from 'src/constants/navigation'

const propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    hash: PropTypes.string,
    search: PropTypes.string,
  }),
}

const AuthCallback = ({ location }) => {
  const dispatch = useDispatch()
  const { hash, search } = location
  const { setExpUserId, setExpUserUTMFields } = useContext(ExpContext)

  if (!hash) {
    redirect(AUTH.SSO_REDIRECT_URI)
    return null
  }

  const {
    state: resourceId,
    type: resourceIndicator,
    unsubscribe: unsubParam,
    targetEl: elementRef,
    targetPath: routeForSource,
    'referrer-source': referrerSource,
    toggleDisclaimer: disclaimerParam,
    customMailerId,
    redirectUrl,
    zipCode,
    utm_id,
    utm_source,
    utm_medium,
    utm_campaign,
  } = qs.parse(search.replace(/^\?/, ''))

  const {
    access_token: token,
    homeDigestUnsubscribe: homeDigestUnsubFragment,
    homeMarketUnsubscribe: homeMarketUnsubFragment,
    listingsUnsubscribe: listingsUnsubFragment,
    managePreferences: managePreferencesFragment,
    unsubscribe: unsubFragment,
    unsubscribeFromCustomer: unsubscribeFromCustomerFragment,
  } = buildUrlParamObj(hash, '#')

  const parseClientId = () =>
    get(parseJWT(token), 'acr.values[0].context', '').replace(/client:/, '') ||
    null

  // Old unsub flows, to be retired but need to support until links expire
  const forHomeowners = resourceIndicator === 'homes'
  const homeDigestUnsubscribe = Boolean(homeDigestUnsubFragment)
  const homeMarketUnsubscribe = Boolean(homeMarketUnsubFragment)
  const listingsUnsubscribe = Boolean(listingsUnsubFragment)
  const unsubscribe = Boolean(unsubParam || unsubFragment)

  const managePreferences = Boolean(managePreferencesFragment)
  const showDisclaimer = Boolean(disclaimerParam)
  const unsubscribeFromCustomer = Boolean(unsubscribeFromCustomerFragment)

  if (unsubscribe && listingsUnsubscribe) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromListingsFlow))
  } else if (unsubscribe && homeMarketUnsubscribe) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromHomeMarketFlow))
  } else if (unsubscribe && homeDigestUnsubscribe) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromHomeDigestFlow))
  } else if (unsubscribe) {
    if (forHomeowners) {
      // unsubscribe link is going to be retired
      // temporary support until old link will expire
      // usubscribe both home digest and home market
      dispatch(flagClientForUnsubscribe(unsubscribeFromHomeownerEmailsFlow))
    } else {
      dispatch(flagClientForUnsubscribe(resourceIndicator))
    }
  } else if (unsubscribeFromCustomer) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromCustomerFlow))
  } else if (showDisclaimer) {
    dispatch(toggleDisclaimer())
  }

  if (!token || !resourceId) {
    redirect(AUTH.SSO_REDIRECT_URI)
    return null
  }

  setClientToken(token)

  setExpUserId(getUserIdFromToken())
  if (utm_id) {
    setExpUserUTMFields({
      utm_id,
      utm_source,
      utm_medium,
      utm_campaign,
    })
  }

  dispatch(setReferrerSource(referrerSource))

  if (customMailerId) {
    dispatch(fetchCustomMailer(customMailerId))
  }

  if (redirectUrl) {
    const path = decodeURIComponent(redirectUrl.replace(/\+/g, ' '))

    return window.location.replace(path)
  }

  if (resourceIndicator === 'chromebot') {
    const path = '/homebot-for-chrome'

    return <Redirect to={path} />
  }

  if (resourceIndicator === 'mls-disclaimers') {
    const path = '/mls-disclaimers'

    return <Redirect to={path} />
  }

  if (resourceIndicator === 'listings-search') {
    return (
      <Redirect
        to={{
          pathname: PATH_HELPERS.listingsSearchRedirect.buildPath(),
          search: PATH_HELPERS.listingsSearchRedirect.buildSearch({
            clientId: parseClientId(),
          }),
        }}
      />
    )
  }

  if (resourceIndicator === 'homes') {
    const clientId = parseClientId()

    let path
    if (clientId && clientId.length === 36) {
      path = PATH_HELPERS.home.buildPath(clientId, resourceId)
      if (managePreferences || unsubscribe || unsubscribeFromCustomer) {
        path += `/preferences`
      }
    } else {
      path = `/reports/home/${resourceId}`
    }

    if (elementRef) {
      path += `#${elementRef}`
    }

    if (routeForSource) {
      path += routeForSource
    }

    return <Redirect to={path} />
  }

  let path
  if (customMailerId) {
    path = PATH_HELPERS.buy.buildPath(resourceId)
  } else if (zipCode) {
    path = PATH_HELPERS.zip.buildPath(resourceId, zipCode)
  } else {
    path = PATH_HELPERS.markets.buildPath(resourceId, '/explore')
  }

  if (managePreferences || unsubscribe || unsubscribeFromCustomer) {
    path = PATH_HELPERS.buy.buildPath(resourceId, '/preferences')
  }
  return <Redirect to={path} />
}

AuthCallback.propTypes = propTypes

export default AuthCallback
